// src/components/gos/ApplicationFullViewModal.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "../../api/axios";
import "./ApplicationFullViewModal.css";

export default function ApplicationFullViewModal({
  open,
  onClose,
  headers,
  t,
  app,
  endpoint = "/backoffice/applications",
  buildUrl,
  locale = "kk",
  activeRole = "",
  onChanged,
}) {
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [dlProgress, setDlProgress] = useState({});
  const [isStaff, setIsStaff] = useState(false);
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [decision, setDecision] = useState(null);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnComment, setReturnComment] = useState("");
  const [returnError, setReturnError] = useState("");

  const fixedLocale = useMemo(() => (["kk", "kz"].includes(locale) ? "kk" : "ru"), [locale]);
  const isKk = fixedLocale === "kk";

  const apiUrl = process.env.REACT_APP_API_URL || "https://expertise.gov.kz";

  const isPublisherMode = useMemo(() => {
    return String(endpoint || "").includes("/publisher/");
  }, [endpoint]);

  const url = useMemo(() => {
    if (!app?.id) return null;
    if (typeof buildUrl === "function") return buildUrl(app);
    return `/${endpoint.replace(/^\/?/, "")}/${app.id}`;
  }, [app, endpoint, buildUrl]);

  const tt = useMemo(() => ({
    kk: {
      applicationInfo: "Өтінім туралы толық ақпарат",
      title: "Атауы",
      registration_number: "Тіркеу нөмірі",
      category: "Санат",
      criteria_form: "Өңдеу формасы",
      status: "Күйі",
      created_at: "Жасалды",
      sent_at: "Жіберілді",
      authors: "Авторы",
      duration_minutes: "Ұзақтығы (мин)",
      page_count: "Бет саны",
      sheet_count: "Парақ саны",
      section_count: "Бөлім саны",
      is_sent: "Жіберілді ме",
      yes: "Иә",
      no: "Жоқ",
      message: "Хабарлама",
      public_status: "Жария статус",
      internal_status: "Ішкі статус",
      archive: "Жалпы архив",
      section_archive: "Секция архиві",
      files: "Файлдар",
      section: "бөлім",
      downloading: "Жүктелуде",
      download: "Жүктеу",
      open_link: "Ашу",
      open_pdf: "PDF ашу",
      close: "Жабу",
      file_no_link: "Файл сілтемесі жоқ",
      file_not_pdf: "Бұл файл PDF емес",
      accept: "Қабылдау",
      return: "Өңдеуге қайтару",
      returnTitle: "Өңдеуге қайтару",
      returnLabel: "Қайтару себебі (міндетті)",
      returnPlaceholder: "Мысалы: файл толық емес...",
      returnConfirm: "Қайтаруды растау",
      cancel: "Болдырмау",
      toastSuccess: "Шешім сәтті қабылданды",
      toastDuplicate: "Бұл өтінішке шешім бұрын қабылданған.",
      errGeneric: "Қате орын алды.",
      required: "Түсіндірме міндетті.",
      loading: "Жүктелуде...",
      notFound: "Өтініш табылмады.",
      decisionLabel: "Шешім",
    },
    ru: {
      applicationInfo: "Полная информация о заявке",
      title: "Название",
      registration_number: "Регистрационный номер",
      category: "Категория",
      criteria_form: "Форма разработки",
      status: "Статус",
      created_at: "Создано",
      sent_at: "Отправлено",
      authors: "Авторы",
      duration_minutes: "Длительность (мин)",
      page_count: "Страниц",
      sheet_count: "Листов",
      section_count: "Разделов",
      is_sent: "Отправлена",
      yes: "Да",
      no: "Нет",
      message: "Сообщение",
      public_status: "Публичный статус",
      internal_status: "Внутренний статус",
      archive: "Общий архив",
      section_archive: "Архив секции",
      files: "Файлы",
      section: "раздел",
      downloading: "Загрузка",
      download: "Скачать",
      open_link: "Открыть",
      open_pdf: "Открыть PDF",
      close: "Закрыть",
      file_no_link: "Нет ссылки на файл",
      file_not_pdf: "Это не PDF-файл",
      accept: "Принять",
      return: "Вернуть на доработку",
      returnTitle: "Возврат на доработку",
      returnLabel: "Причина возврата (обязательно)",
      returnPlaceholder: "Например: неполный файл...",
      returnConfirm: "Подтвердить возврат",
      cancel: "Отменить",
      toastSuccess: "Решение успешно принято",
      toastDuplicate: "По этой заявке уже есть решение.",
      errGeneric: "Произошла ошибка.",
      required: "Комментарий обязателен.",
      loading: "Загрузка...",
      notFound: "Заявка не найдена.",
      decisionLabel: "Решение",
    },
  })[fixedLocale], [fixedLocale]);

  /* ── progress helpers ────────────────────────────────────── */
  const setProgress = useCallback((key, val) => {
    setDlProgress((p) => ({ ...p, [key]: val }));
  }, []);

  const clearProgress = useCallback((key) => {
    setDlProgress((p) => {
      const c = { ...p };
      delete c[key];
      return c;
    });
  }, []);

  const progressState = (key) => {
    const val = dlProgress[key];
    const hasValue = Number.isFinite(val);

    return {
      isLoading: hasValue && val < 100,
      hasPct: hasValue && val > 0,
      pct: hasValue ? val : 0,
    };
  };

  /* ── utils ───────────────────────────────────────────────── */
  const pickLocale = useCallback((val) => {
    if (val == null) return "—";
    if (typeof val === "string" || typeof val === "number") return String(val);

    if (typeof val === "object") {
      const kz =
        val.kk ??
        val.kz ??
        val.name_kz ??
        val.title_kz ??
        val.label_kz ??
        val.value_kz ??
        val.status_kz;

      const ru =
        val.ru ??
        val.name_ru ??
        val.title_ru ??
        val.label_ru ??
        val.value_ru ??
        val.status_ru;

      const primary = fixedLocale === "kk" ? (kz ?? ru) : (ru ?? kz);

      if (primary != null) return String(primary);

      for (const v of Object.values(val)) {
        if (typeof v === "string" || typeof v === "number") return String(v);
      }

      return "—";
    }

    return String(val);
  }, [fixedLocale]);

  const pickField = (...vals) => {
    for (const v of vals) {
      if (v == null) continue;
      if (typeof v === "string" && v.trim() === "") continue;
      if (typeof v === "string" && ["null", "none", "undefined"].includes(v.trim().toLowerCase())) continue;
      return v;
    }

    return null;
  };

  const formatDate = (s) => {
    if (!s) return "—";

    const d = new Date(s);

    if (Number.isNaN(d.getTime())) return String(s);

    return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
  };

  const getApiOrigin = useCallback(() => {
    try {
      return new URL(apiUrl).origin;
    } catch {
      return window.location.origin;
    }
  }, [apiUrl]);

  const absoluteUrl = useCallback((u) => {
    if (!u) return "";

    const cleaned = String(u).trim();

    if (/^https?:\/\//i.test(cleaned)) {
      return cleaned.replace(/^http:\/\//i, "https://");
    }

    const origin = getApiOrigin();

    return `${origin}${cleaned.startsWith("/") ? cleaned : `/${cleaned}`}`;
  }, [getApiOrigin]);

  const isExternalUrl = useCallback((u) => {
    if (!u) return false;

    const full = absoluteUrl(u);

    try {
      const target = new URL(full);
      const apiOrigin = getApiOrigin();

      return target.origin !== apiOrigin;
    } catch {
      return false;
    }
  }, [absoluteUrl, getApiOrigin]);

  const isSignedStorageUrl = useCallback((u) => {
    const full = absoluteUrl(u).toLowerCase();

    return (
      full.includes("x-amz-signature=") ||
      full.includes("x-amz-credential=") ||
      full.includes("x-amz-algorithm=") ||
      full.includes("minio.")
    );
  }, [absoluteUrl]);

  const toAxiosUrl = useCallback((u) => {
    if (!u) return "";

    const raw = String(u).trim();

    if (raw.startsWith("/api/")) {
      return raw.replace(/^\/api/, "");
    }

    if (raw.startsWith("/")) {
      return raw;
    }

    const full = absoluteUrl(raw);

    try {
      const target = new URL(full);
      const apiOrigin = getApiOrigin();

      if (target.origin === apiOrigin && target.pathname.startsWith("/api/")) {
        return `${target.pathname.replace(/^\/api/, "")}${target.search}`;
      }

      return full;
    } catch {
      return raw;
    }
  }, [absoluteUrl, getApiOrigin]);

  const sanitizeFilename = (name) =>
    name ? (String(name).replace(/[<>:"/\\|?*\x00-\x1F]/g, "_").trim() || null) : null;

  const saveBlob = (blob, filename = "file") => {
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = u;
    a.download = filename;

    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(u);
  };

const openBlobInNewTab = (blob, fallbackType = "application/pdf") => {
  const type = blob?.type || fallbackType;
  const fileBlob = new Blob([blob], { type });
  const u = URL.createObjectURL(fileBlob);

  const a = document.createElement("a");
  a.href = u;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();

  setTimeout(() => URL.revokeObjectURL(u), 120000);
};

  const parseDisposition = (disp) => {
    if (!disp) return null;

    const mStar = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(disp);

    if (mStar?.[1]) {
      try {
        return decodeURIComponent(mStar[1].replace(/^["']|["']$/g, ""));
      } catch {
        return mStar[1].replace(/^["']|["']$/g, "");
      }
    }

    const m = /filename="?([^"]+)"?/i.exec(disp);

    return m ? m[1] : null;
  };

  const looksLikePdf = (file) => {
    const values = [
      file?.mime_type,
      file?.mime,
      file?.content_type,
      file?.type,
      file?.name,
      file?.application_file_name,
      file?.original_name,
      file?.download_url,
      file?.url,
      file?.file_link,
      file?.link_url,
    ]
      .filter(Boolean)
      .map((x) => String(x).toLowerCase());

    return values.some((v) => v.includes("application/pdf") || v.endsWith(".pdf") || v.includes(".pdf?"));
  };

  const normalizeApplication = useCallback((payload) => {
    if (!payload || typeof payload !== "object") return null;

    const a = payload?.application ?? payload;

    if (!a || typeof a !== "object") return null;

    return {
      ...a,
      files: Array.isArray(a.files) ? a.files : [],
      group: a.group && typeof a.group === "object" ? a.group : (a.group ?? null),
      archive: a.archive && typeof a.archive === "object" ? a.archive : (a.archive ?? null),
      internal_status: a.internal_status ?? null,
      public_status: a.public_status ?? null,
      criteria_form: a.criteria_form ?? null,
      category: a.category ?? null,
    };
  }, []);

  const dlHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${localStorage.getItem("token")}`,
      "Accept-Language": fixedLocale,
    }),
    [fixedLocale]
  );

  const browserDownloadNoAuth = useCallback((u, filename = "") => {
    const full = absoluteUrl(u);

    const a = document.createElement("a");
    a.href = full;
    a.target = "_blank";
    a.rel = "noopener noreferrer";

    if (filename) {
      a.download = filename;
    }

    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [absoluteUrl]);

  const browserOpenNoAuth = useCallback((u) => {
    const full = absoluteUrl(u);
    window.open(full, "_blank", "noopener,noreferrer");
  }, [absoluteUrl]);

  const readBlobAsJson = async (blob) => {
    try {
      const text = await blob.text();
      return JSON.parse(text);
    } catch {
      return null;
    }
  };

  const requestBlob = useCallback(async ({
    url: requestUrl,
    pKey,
    accept = "application/octet-stream",
    onProgress = true,
  }) => {
    const res = await axios.get(toAxiosUrl(requestUrl), {
      headers: {
        ...dlHeaders,
        Accept: accept,
      },
      responseType: "blob",
      onDownloadProgress: onProgress
        ? (e) => {
            setProgress(
              pKey,
              e.total ? Math.min(100, Math.round((e.loaded / e.total) * 100)) : 0
            );
          }
        : undefined,
    });

    return res;
  }, [dlHeaders, setProgress, toAxiosUrl]);

  const getFileDownloadPath = useCallback((fileId) => {
    return isPublisherMode
      ? `/publisher/files/${fileId}/download`
      : `/backoffice/files/${fileId}/download`;
  }, [isPublisherMode]);

  const getSectionArchivePath = useCallback((fileId) => {
    return isPublisherMode
      ? `/publisher/files/${fileId}/section-archive`
      : `/backoffice/files/${fileId}/section-archive`;
  }, [isPublisherMode]);

  const getApplicationArchivePath = useCallback((appId) => {
    return isPublisherMode
      ? `/publisher/applications/${appId}/download-archive`
      : `/backoffice/applications/${appId}/download-archive`;
  }, [isPublisherMode]);

  /* ── download / open helpers ─────────────────────────────── */
  const downloadByUrl = useCallback(async (u, suggestedName = "file", pKey = "download") => {
    if (!u) return;

    try {
      setProgress(pKey, 0);

      if (isExternalUrl(u) || isSignedStorageUrl(u)) {
        browserDownloadNoAuth(u, suggestedName);
        setProgress(pKey, 100);
        setTimeout(() => clearProgress(pKey), 700);
        return;
      }

      const res = await requestBlob({
        url: u,
        pKey,
        accept: "application/octet-stream",
      });

      saveBlob(
        res.data,
        parseDisposition(res.headers?.["content-disposition"]) || suggestedName
      );

      setProgress(pKey, 100);
      setTimeout(() => clearProgress(pKey), 700);
    } catch {
      alert(isKk ? "❌ Файлды жүктеу мүмкін болмады" : "❌ Не удалось скачать файл");
      clearProgress(pKey);
    }
  }, [
    browserDownloadNoAuth,
    clearProgress,
    isExternalUrl,
    isKk,
    isSignedStorageUrl,
    requestBlob,
    setProgress,
  ]);

  const openPdfByUrl = useCallback(async (u, pKey = "open-pdf") => {
    if (!u) return;

    try {
      setProgress(pKey, 0);

      if (isExternalUrl(u) || isSignedStorageUrl(u)) {
        browserOpenNoAuth(u);
        setProgress(pKey, 100);
        setTimeout(() => clearProgress(pKey), 700);
        return;
      }

      const res = await requestBlob({
        url: u,
        pKey,
        accept: "application/pdf",
      });

      openBlobInNewTab(res.data, "application/pdf");

      setProgress(pKey, 100);
      setTimeout(() => clearProgress(pKey), 700);
    } catch {
      alert(isKk ? "❌ PDF ашу мүмкін болмады" : "❌ Не удалось открыть PDF");
      clearProgress(pKey);
    }
  }, [
    browserOpenNoAuth,
    clearProgress,
    isExternalUrl,
    isKk,
    isSignedStorageUrl,
    requestBlob,
    setProgress,
  ]);

  const downloadFileById = useCallback(async (fileId, suggestedName = "file", pKey) => {
    if (!fileId) return;

    try {
      setProgress(pKey, 0);

      const res = await requestBlob({
        url: getFileDownloadPath(fileId),
        pKey,
        accept: "application/octet-stream",
      });

      saveBlob(
        res.data,
        parseDisposition(res.headers?.["content-disposition"]) || suggestedName
      );

      setProgress(pKey, 100);
      setTimeout(() => clearProgress(pKey), 700);
    } catch {
      alert(isKk ? "❌ Файлды жүктеу мүмкін болмады" : "❌ Не удалось скачать файл");
      clearProgress(pKey);
    }
  }, [
    clearProgress,
    getFileDownloadPath,
    isKk,
    requestBlob,
    setProgress,
  ]);

  const openPdfFileById = useCallback(async (fileId, pKey) => {
    if (!fileId) return;

    try {
      setProgress(pKey, 0);

      const res = await requestBlob({
        url: getFileDownloadPath(fileId),
        pKey,
        accept: "application/pdf",
      });

      openBlobInNewTab(res.data, "application/pdf");

      setProgress(pKey, 100);
      setTimeout(() => clearProgress(pKey), 700);
    } catch {
      alert(isKk ? "❌ PDF ашу мүмкін болмады" : "❌ Не удалось открыть PDF");
      clearProgress(pKey);
    }
  }, [
    clearProgress,
    getFileDownloadPath,
    isKk,
    requestBlob,
    setProgress,
  ]);

  const downloadArchive = useCallback(async (appId, archiveUrl, suggestedName, pKey) => {
    if (!appId && !archiveUrl) return;

    try {
      setProgress(pKey, 0);

      if (appId) {
        const res = await requestBlob({
          url: getApplicationArchivePath(appId),
          pKey,
          accept: "application/octet-stream",
        });

        const contentType = String(res.headers?.["content-type"] || "").toLowerCase();

        if (contentType.includes("application/json")) {
          const json = await readBlobAsJson(res.data);
          const signedUrl = json?.url || json?.download_url || json?.temporary_url;

          if (signedUrl) {
            browserDownloadNoAuth(signedUrl, suggestedName);
            setProgress(pKey, 100);
            setTimeout(() => clearProgress(pKey), 700);
            return;
          }
        }

        saveBlob(
          res.data,
          parseDisposition(res.headers?.["content-disposition"]) || suggestedName || `archive_${appId}.zip`
        );

        setProgress(pKey, 100);
        setTimeout(() => clearProgress(pKey), 700);
        return;
      }

      if (archiveUrl) {
        await downloadByUrl(archiveUrl, suggestedName, pKey);
      }
    } catch {
      if (archiveUrl) {
        await downloadByUrl(archiveUrl, suggestedName, pKey);
        return;
      }

      alert(isKk ? "❌ Архивті жүктеу мүмкін болмады" : "❌ Не удалось скачать архив");
      clearProgress(pKey);
    }
  }, [
    browserDownloadNoAuth,
    clearProgress,
    downloadByUrl,
    getApplicationArchivePath,
    isKk,
    requestBlob,
    setProgress,
  ]);

  const downloadSectionArchive = useCallback(async (file, suggestedName, pKey) => {
    try {
      setProgress(pKey, 0);

      if (file?.id) {
        const res = await requestBlob({
          url: getSectionArchivePath(file.id),
          pKey,
          accept: "application/octet-stream",
        });

        const contentType = String(res.headers?.["content-type"] || "").toLowerCase();

        if (contentType.includes("application/json")) {
          const json = await readBlobAsJson(res.data);
          const signedUrl = json?.url || json?.download_url || json?.temporary_url;

          if (signedUrl) {
            browserDownloadNoAuth(signedUrl, suggestedName);
            setProgress(pKey, 100);
            setTimeout(() => clearProgress(pKey), 700);
            return;
          }
        }

        saveBlob(
          res.data,
          parseDisposition(res.headers?.["content-disposition"]) || suggestedName
        );

        setProgress(pKey, 100);
        setTimeout(() => clearProgress(pKey), 700);
        return;
      }

      if (file?.section_archive_download_url) {
        await downloadByUrl(file.section_archive_download_url, suggestedName, pKey);
        return;
      }

      alert(tt.file_no_link);
      clearProgress(pKey);
    } catch {
      if (file?.section_archive_download_url) {
        await downloadByUrl(file.section_archive_download_url, suggestedName, pKey);
        return;
      }

      alert(isKk ? "❌ Секция архивін жүктеу мүмкін болмады" : "❌ Не удалось скачать архив секции");
      clearProgress(pKey);
    }
  }, [
    browserDownloadNoAuth,
    clearProgress,
    downloadByUrl,
    getSectionArchivePath,
    isKk,
    requestBlob,
    setProgress,
    tt.file_no_link,
  ]);

  /* ── reload ──────────────────────────────────────────────── */
  const reloadApplication = useCallback(async () => {
    if (!url) return;

    try {
      const res = await axios.get(url, { headers });
      setApplication(normalizeApplication(res.data));
    } catch {}
  }, [url, headers, normalizeApplication]);

  /* ── decision ────────────────────────────────────────────── */
  const formatDecision = useCallback((d) => {
    const raw = d?.status ?? d;

    if (!raw || raw === "null" || raw === "None") return "—";

    const v = String(raw).toLowerCase().trim();

    if (v === "accept") return isKk ? "Қабылданды" : "Принято";
    if (v === "return") return isKk ? "Өңдеуге қайтарылды" : "Возвращено на доработку";
    if (v === "reject_expertise") return isKk ? "Сараптамадан бас тартылды" : "Отказано в экспертизе";

    return String(raw);
  }, [isKk]);

  const decisionVariant = (d) => {
    const raw = d?.status ?? d;
    const v = String(raw || "").toLowerCase().trim();

    if (v === "accept") return "success";
    if (v === "return") return "warning";
    if (v === "reject_expertise") return "danger";

    return "neutral";
  };

  const handleDecision = async (decisionType) => {
    if (!application?.id) return;

    if (decisionType === "return") {
      setReturnError("");
      setShowReturnModal(true);
      return;
    }

    setDecisionLoading(true);

    try {
      const res = await axios.post(
        `/backoffice/applications/${application.id}/decision`,
        { decision: decisionType },
        { headers }
      );

      alert(res.data?.message || tt.toastSuccess);
      setDecision({ status: decisionType });

      await reloadApplication();
      onChanged?.();
    } catch (error) {
      const msg = error.response?.data?.message;
      const status = error.response?.status;

      alert(status === 409 ? (msg || tt.toastDuplicate) : (msg || tt.errGeneric));
    } finally {
      setDecisionLoading(false);
    }
  };

  const submitReturnDecision = async () => {
    const comment = returnComment.trim();

    if (!comment) {
      setReturnError(tt.required);
      return;
    }

    setDecisionLoading(true);
    setReturnError("");

    try {
      const res = await axios.post(
        `/backoffice/applications/${application.id}/decision`,
        { decision: "return", comment },
        { headers }
      );

      alert(res.data?.message || tt.toastSuccess);

      setShowReturnModal(false);
      setReturnComment("");
      setDecision({ status: "return", comment });

      await reloadApplication();
      onChanged?.();
    } catch (error) {
      const msg = error.response?.data?.message;
      const status = error.response?.status;

      setReturnError(status === 409 ? (msg || tt.toastDuplicate) : (msg || tt.errGeneric));
    } finally {
      setDecisionLoading(false);
    }
  };

  const closeReturnModal = () => {
    setShowReturnModal(false);
    setReturnComment("");
    setReturnError("");
  };

  /* ── effects ─────────────────────────────────────────────── */
  useEffect(() => {
    if (!open || !url) {
      setApplication(null);
      setErr("");
      return;
    }

    const ctrl = new AbortController();

    setLoading(true);
    setErr("");
    setApplication(null);
    setDecision(null);

    (async () => {
      try {
        const res = await axios.get(url, {
          headers,
          signal: ctrl.signal,
        });

        setApplication(normalizeApplication(res.data));
      } catch (e) {
        if (e?.name !== "CanceledError" && e?.code !== "ERR_CANCELED") {
          setErr(e?.response?.data?.message || e?.message || t?.err || "Error");
        }
      } finally {
        setLoading(false);
      }
    })();

    return () => ctrl.abort();
  }, [open, url, headers, normalizeApplication, t?.err]);

  useEffect(() => {
    if (!open) return;

    const DEPT = "Сараптаманы жоспарлау және мемлекеттік қызметтер бөлімі";

    (async () => {
      try {
        const res = await axios.get("/internal/profile", { headers });
        setIsStaff(activeRole === "staff" && (res.data?.department?.name || "") === DEPT);
      } catch {
        setIsStaff(false);
      }
    })();
  }, [open, headers, activeRole]);

  useEffect(() => {
    if (!open) return;

    const onEsc = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onEsc);

    return () => window.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;

    const prev = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  /* ── render data ─────────────────────────────────────────── */
  const g = application?.group && typeof application.group === "object" ? application.group : {};

  const registrationNumber = pickField(
    application?.registration_number,
    application?.group_registration_number,
    application?.application_group_registration_number,
    g?.registration_number,
    app?.registration_number,
    app?.group?.registration_number
  );

  const safeMessage =
    !application?.message || ["null", "None"].includes(String(application.message))
      ? "—"
      : String(application.message);

  const archiveObj =
    application?.archive && typeof application.archive === "object"
      ? application.archive
      : null;

  const hasArchive = Boolean(
    archiveObj &&
      (
        archiveObj.id ||
        archiveObj.original_name ||
        archiveObj.name ||
        archiveObj.url ||
        archiveObj.download_url
      )
  ) || Boolean(application?.archive_path || application?.id);

  const archiveDirectUrl = archiveObj?.download_url || archiveObj?.url || null;
  const archiveName = archiveObj?.original_name || archiveObj?.name || `archive_${application?.id || "file"}.zip`;
  const archivePS = progressState("archive");

  const groupStatusLocalized =
    g?.status === "submitted"
      ? (isKk ? "Жіберілді" : "Отправлено")
      : g?.status
        ? String(g.status)
        : null;

  const currentDecisionRaw =
    application?.decision?.status ??
    application?.decision ??
    decision?.status ??
    null;

  const currentDecisionComment =
    application?.decision?.comment ??
    decision?.comment ??
    null;

  const hasDecision = Boolean(
    currentDecisionRaw &&
      currentDecisionRaw !== "null" &&
      currentDecisionRaw !== "None"
  );

  const rows = application ? [
    [tt.title, pickField(application.title, g.title) || "—"],
    [tt.registration_number, registrationNumber || "—"],
    [tt.category, pickLocale(application.category)],
    [tt.criteria_form, pickLocale(application.criteria_form)],
    [tt.public_status, pickLocale(pickField(application.public_status, application.status, g.public_status, groupStatusLocalized))],
    [tt.internal_status, pickLocale(pickField(application.internal_status, g.internal_status))],
    [tt.sent_at, formatDate(pickField(application.sent_at, g.sent_at))],
    [tt.authors, pickField(application.authors) ?? "—"],
    [tt.duration_minutes, application.duration_minutes ?? "—"],
    [tt.page_count, application.page_count ?? "—"],
    [tt.sheet_count, application.sheet_count ?? "—"],
    [tt.section_count, application.section_count ?? "—"],
    [tt.is_sent, application.is_sent ? tt.yes : tt.no],
    [tt.message, safeMessage],
  ].filter(([, v]) => v && v !== "—") : [];

  const modalUi = (
    <div className="afvm-overlay" role="dialog" aria-modal="true" onMouseDown={onClose}>
      <div className="afvm-modal" onMouseDown={(e) => e.stopPropagation()}>

        {/* HEADER */}
        <div className="afvm-head">
          <div className="afvm-head__meta">
            <h2 className="afvm-heading">{t?.appModalTitle || tt.applicationInfo}</h2>


          </div>

          <button type="button" className="afvm-x" onClick={onClose} aria-label={tt.close}>
            ×
          </button>
        </div>

        {/* BODY */}
        <div className="afvm-body">
          {loading ? (
            <div className="afvm-state">{tt.loading}</div>
          ) : err ? (
            <div className="afvm-state afvm-state--error">{err}</div>
          ) : !application ? (
            <div className="afvm-state">{tt.notFound}</div>
          ) : (
            <>
              {/* Decision banner */}
              {hasDecision && (
                <div className={`afvm-banner afvm-banner--${decisionVariant(currentDecisionRaw)}`}>
                  <span className="afvm-banner__label">{tt.decisionLabel}</span>
                  <span className="afvm-banner__value">{formatDecision(currentDecisionRaw)}</span>

                  {currentDecisionComment && (
                    <span className="afvm-banner__comment">{currentDecisionComment}</span>
                  )}
                </div>
              )}

              {/* Info table */}
              <dl className="afvm-dl">
                {rows.map(([label, value], i) => (
                  <div key={`${label}-${i}`} className="afvm-dl__row">
                    <dt className="afvm-dl__label">{label}</dt>
                    <dd className="afvm-dl__value">{value}</dd>
                  </div>
                ))}

                {application?.word && (
  <div className="afvm-dl__row">
    <dt className="afvm-dl__label">
      {isKk ? "Доработка файлы" : "Файл доработки"}
    </dt>
    <dd className="afvm-dl__value">
      <div className="afvm-dl-wrap">
        {(() => {
          const wordPS = progressState("word");
          const wordName = application.word?.original_name || "document.docx";
          return (
            <>
              <button
                className={`afvm-action-btn ${wordPS.isLoading ? "afvm-action-btn--loading" : ""}`}
                type="button"
                disabled={wordPS.isLoading}
                onClick={() => downloadByUrl(
                  `/applications/${application.id}/download-word`,
                  wordName,
                  "word"
                )}
              >
                {wordPS.isLoading ? (
                  <>
                    <span className="afvm-spinner" />
                    {tt.downloading}{wordPS.hasPct ? ` ${wordPS.pct}%` : "..."}
                  </>
                ) : (
                  <>
                    <span className="afvm-btn-icon">↓</span>
                    {wordName}
                  </>
                )}
              </button>
              {wordPS.isLoading && (
                <div className="afvm-progress">
                  <div
                    className={`afvm-progress__bar ${wordPS.hasPct ? "" : "is-indeterminate"}`}
                    style={wordPS.hasPct ? { width: `${wordPS.pct}%` } : undefined}
                  />
                </div>
              )}
            </>
          );
        })()}
      </div>
    </dd>
  </div>
)}

                {/* Общий архив заявки */}
                {hasArchive && (
                  <div className="afvm-dl__row">
                    <dt className="afvm-dl__label">{tt.archive}</dt>

                    <dd className="afvm-dl__value">
                      <div className="afvm-dl-wrap">
                        <button
                          className={`afvm-action-btn afvm-action-btn--archive ${archivePS.isLoading ? "afvm-action-btn--loading" : ""}`}
                          type="button"
                          disabled={archivePS.isLoading}
                          onClick={() => downloadArchive(application.id, archiveDirectUrl, archiveName, "archive")}
                        >
                          {archivePS.isLoading ? (
                            <>
                              <span className="afvm-spinner" />
                              {tt.downloading}{archivePS.hasPct ? ` ${archivePS.pct}%` : "..."}
                            </>
                          ) : (
                            <>
                              <span className="afvm-btn-icon">↓</span>
                              {tt.download} — {archiveName}
                            </>
                          )}
                        </button>

                        {archivePS.isLoading && (
                          <div className="afvm-progress">
                            <div
                              className={`afvm-progress__bar ${archivePS.hasPct ? "" : "is-indeterminate"}`}
                              style={archivePS.hasPct ? { width: `${archivePS.pct}%` } : undefined}
                            />
                          </div>
                        )}
                      </div>
                    </dd>
                  </div>
                )}
              </dl>

              {/* Files */}
              {application.files.length > 0 && (
                <div className="afvm-files">
                  <h3 className="afvm-files__heading">{tt.files}</h3>

                  <ul className="afvm-files__list">
                    {application.files.map((file, index) => {
                      const sectionNum = file.section ?? index + 1;
                      const sectionLabel = `${sectionNum} ${tt.section}`;

                      const displayName =
                        (typeof file.name === "string" && file.name.trim()) ||
                        (typeof file.application_file_name === "string" && file.application_file_name.trim()) ||
                        (typeof file.original_name === "string" && file.original_name.trim()) ||
                        sectionLabel;

                      const isLink = Boolean(file.is_link || file.link_url || file.file_link);
                      const linkHref = file.link_url || file.file_link || file.url;

                      const dlUrl = file.download_url || file.url;

                      const pKey = file.id ? `id:${file.id}` : `idx:${index}`;
                      const openPdfKey = file.id ? `open-pdf:${file.id}` : `open-pdf:${index}`;

                      const ps = progressState(pKey);
                      const openPdfPS = progressState(openPdfKey);

                      const suggestedName = sanitizeFilename(displayName) || `file_${sectionNum}`;
                      const isPdf = looksLikePdf(file);

                      const hasSectionArchive = Boolean(
                        file.has_section_archive ||
                          file.section_archive_name ||
                          file.section_archive_download_url ||
                          file.section_archive?.download_url
                      );

                      const sectionArchiveKey = file.id
                        ? `section-archive:${file.id}`
                        : `section-archive:${index}`;

                      const sectionArchivePS = progressState(sectionArchiveKey);

                      const sectionArchiveName =
                        file.section_archive_name ||
                        file.section_archive?.original_name ||
                        `section_${sectionNum}.zip`;

                      const sectionArchiveFile = {
                        ...file,
                        section_archive_download_url:
                          file.section_archive_download_url ||
                          file.section_archive?.download_url,
                      };

                      return (
                        <li key={file.id || index} className="afvm-file">
                          <div className="afvm-file__info">
                            <span className={`afvm-file__badge ${isLink ? "afvm-file__badge--link" : "afvm-file__badge--file"}`}>
                              {isLink ? "LINK" : isPdf ? "PDF" : "FILE"}
                            </span>

                            <span className="afvm-file__name" title={displayName}>
                              {displayName}
                            </span>
                          </div>

                          <div className="afvm-file__actions">
                            {isLink ? (
                              <>
                                <button
                                  type="button"
                                  className="afvm-action-btn afvm-action-btn--ghost"
                                  onClick={() => browserOpenNoAuth(linkHref)}
                                >
                                  <svg width="13" height="13" viewBox="0 0 14 14" fill="none" style={{ marginRight: 4, flexShrink: 0 }}>
                                    <path d="M1 7s2.5-4 6-4 6 4 6 4-2.5 4-6 4-6-4-6-4Z" stroke="currentColor" strokeWidth="1.5" />
                                    <circle cx="7" cy="7" r="1.5" fill="currentColor" />
                                  </svg>

                                  {tt.open_link}
                                </button>

                                {isPdf && (
                                  <button
                                    type="button"
                                    className={`afvm-action-btn afvm-action-btn--ghost ${openPdfPS.isLoading ? "afvm-action-btn--loading" : ""}`}
                                    disabled={openPdfPS.isLoading}
                                    onClick={() => openPdfByUrl(linkHref, openPdfKey)}
                                  >
                                    {openPdfPS.isLoading ? (
                                      <>
                                        <span className="afvm-spinner" />
                                        {tt.downloading}...
                                      </>
                                    ) : (
                                      <>
                                        <span className="afvm-btn-icon">↗</span>
                                        {tt.open_pdf}
                                      </>
                                    )}
                                  </button>
                                )}
                              </>
                            ) : (
                              <>
                                {isPdf && (
                                  <div className="afvm-dl-wrap">
                                    <button
                                      type="button"
                                      className={`afvm-action-btn afvm-action-btn--ghost ${openPdfPS.isLoading ? "afvm-action-btn--loading" : ""}`}
                                      disabled={openPdfPS.isLoading || (!file.id && !dlUrl)}
                                      onClick={() => {
                                        if (file.id) return openPdfFileById(file.id, openPdfKey);
                                        if (dlUrl) return openPdfByUrl(dlUrl, openPdfKey);
                                        alert(tt.file_no_link);
                                        return null;
                                      }}
                                    >
                                      {openPdfPS.isLoading ? (
                                        <>
                                          <span className="afvm-spinner" />
                                          {tt.downloading}{openPdfPS.hasPct ? ` ${openPdfPS.pct}%` : "..."}
                                        </>
                                      ) : (
                                        <>
                                          <span className="afvm-btn-icon">↗</span>
                                          {tt.open_pdf}
                                        </>
                                      )}
                                    </button>

                                    {openPdfPS.isLoading && (
                                      <div className="afvm-progress">
                                        <div
                                          className={`afvm-progress__bar ${openPdfPS.hasPct ? "" : "is-indeterminate"}`}
                                          style={openPdfPS.hasPct ? { width: `${openPdfPS.pct}%` } : undefined}
                                        />
                                      </div>
                                    )}
                                  </div>
                                )}

                                <div className="afvm-dl-wrap">
                                  <button
                                    type="button"
                                    className={`afvm-action-btn ${ps.isLoading ? "afvm-action-btn--loading" : ""}`}
                                    disabled={ps.isLoading || (!file.id && !dlUrl)}
                                    onClick={() => {
                                      if (file.id) return downloadFileById(file.id, suggestedName, pKey);
                                      if (dlUrl) return downloadByUrl(dlUrl, suggestedName, pKey);
                                      alert(tt.file_no_link);
                                      return null;
                                    }}
                                  >
                                    {ps.isLoading ? (
                                      <>
                                        <span className="afvm-spinner" />
                                        {tt.downloading}{ps.hasPct ? ` ${ps.pct}%` : "..."}
                                      </>
                                    ) : (
                                      <>
                                        <span className="afvm-btn-icon">↓</span>
                                        {tt.download}
                                      </>
                                    )}
                                  </button>

                                  {ps.isLoading && (
                                    <div className="afvm-progress">
                                      <div
                                        className={`afvm-progress__bar ${ps.hasPct ? "" : "is-indeterminate"}`}
                                        style={ps.hasPct ? { width: `${ps.pct}%` } : undefined}
                                      />
                                    </div>
                                  )}
                                </div>
                              </>
                            )}

                            {/* Архив секции этого файла */}
                            {hasSectionArchive && (
                              <div className="afvm-dl-wrap">
                                <button
                                  type="button"
                                  className={`afvm-action-btn afvm-action-btn--section-archive ${sectionArchivePS.isLoading ? "afvm-action-btn--loading" : ""}`}
                                  disabled={sectionArchivePS.isLoading}
                                  onClick={() => downloadSectionArchive(sectionArchiveFile, sectionArchiveName, sectionArchiveKey)}
                                >
                                  {sectionArchivePS.isLoading ? (
                                    <>
                                      <span className="afvm-spinner" />
                                      {tt.downloading}...
                                    </>
                                  ) : (
                                    <>
                                      <span className="afvm-btn-icon">↓</span>
                                      {tt.section_archive}
                                    </>
                                  )}
                                </button>

                                {sectionArchivePS.isLoading && (
                                  <div className="afvm-progress">
                                    <div
                                      className={`afvm-progress__bar ${sectionArchivePS.hasPct ? "" : "is-indeterminate"}`}
                                      style={sectionArchivePS.hasPct ? { width: `${sectionArchivePS.pct}%` } : undefined}
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        {/* FOOTER */}
        <div className="afvm-foot">
          {isStaff && (!hasDecision || String(currentDecisionRaw).toLowerCase() === "return") && application && (
            <div className="afvm-decision-btns">
              <button
                className="afvm-action-btn afvm-action-btn--accept"
                onClick={() => handleDecision("accept")}
                disabled={decisionLoading}
              >
                {decisionLoading ? <span className="afvm-spinner" /> : tt.accept}
              </button>

              <button
                className="afvm-action-btn afvm-action-btn--return"
                onClick={() => handleDecision("return")}
                disabled={decisionLoading}
              >
                {tt.return}
              </button>
            </div>
          )}

          <button className="afvm-action-btn afvm-action-btn--ghost" onClick={onClose}>
            {tt.close}
          </button>
        </div>
      </div>

      {/* RETURN MODAL */}
      {showReturnModal && (
        <div className="afvm-return-overlay" onMouseDown={closeReturnModal}>
          <div className="afvm-return-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="afvm-return-head">
              <h3 className="afvm-return-heading">{tt.returnTitle}</h3>

              <button type="button" className="afvm-x" onClick={closeReturnModal} aria-label={tt.cancel}>
                ×
              </button>
            </div>

            <div className="afvm-return-body">
              <label className="afvm-return-label" htmlFor="afvm-return-comment">
                {tt.returnLabel}
              </label>

              <textarea
                id="afvm-return-comment"
                className="afvm-return-textarea"
                value={returnComment}
                onChange={(e) => setReturnComment(e.target.value)}
                maxLength={2000}
                rows={5}
                placeholder={tt.returnPlaceholder}
                disabled={decisionLoading}
              />

              <div className="afvm-return-meta">
                <span className="afvm-return-count">{returnComment.length} / 2000</span>
                {returnError && <span className="afvm-return-error">{returnError}</span>}
              </div>
            </div>

            <div className="afvm-return-foot">
              <button
                className="afvm-action-btn afvm-action-btn--return"
                onClick={submitReturnDecision}
                disabled={decisionLoading || !returnComment.trim()}
              >
                {decisionLoading ? <span className="afvm-spinner" /> : tt.returnConfirm}
              </button>

              <button
                className="afvm-action-btn afvm-action-btn--ghost"
                onClick={closeReturnModal}
                disabled={decisionLoading}
              >
                {tt.cancel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(modalUi, document.body);
}